import { motion } from "framer-motion";
import chroma from "chroma-js";
import { useAtom } from "jotai";
import React, { useEffect, useRef } from "react";
import { Spring } from "wobble";
import { BORDER_WIDTH } from "../blocks/Block";
import Icon from "../components/Icon";
import {
  BLOCK_Z_UPPERBOUND,
  DEFAULT_CURSOR_OFFSET,
  DEFAULT_CURSOR_SIZE,
  DEFAULT_GRID_RESOLUTION,
  DEFAULT_SPRING,
  GRID_SEAM,
} from "../consts";
import { colorValue } from "../css/color";
import { defaultSpringAtom } from "../jotai/atoms";
import { useBlocks, useSpaceInfo, useUserInfo } from "../liveblocks/maps";
import { styled } from "../stitches.config";
import {
  AnyBlock,
  Point2D,
  Size,
  User,
  UserLocation,
  Presence,
} from "../types";
import { timestampToZIndex } from "../utils/updatedAt";
import { useAnimationFrame } from "../utils/useAnimationFrame";
import { StampMaker } from "./useStamp";

export default function Stream({
  location,
  cursorPosRef,
  color,
  userId,
  activeStampMode,
  stampMode,
  upsertBlock,
  stream,
}: {
  stream: MediaStreamTrack;
  location: UserLocation | undefined;
  cursorPosRef: React.MutableRefObject<Point2D>;
  color: User["color"];
  userId: string;
  activeStampMode: boolean;
  stampMode: Presence["stampMode"];
  upsertBlock: (block: Partial<AnyBlock>) => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeStampModeRef = useRef(activeStampMode);
  activeStampModeRef.current = activeStampMode;

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.srcObject = new MediaStream([stream]);
    }
  }, [videoRef, stream]);

  const [spring] = useAtom(defaultSpringAtom);
  const [spaceInfo] = useSpaceInfo();
  const cursorSize = spaceInfo?.cursorSize || DEFAULT_CURSOR_SIZE;

  // Track previous frame's block ID to prevent animation when moving/resizing the same block
  const previousBlockId = useRef<string | undefined>(undefined);
  const isMovingOrResizing = useRef<boolean>(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const sizeContainerRef = useRef<HTMLDivElement>(null);
  const targetPositionRef = useRef<Point2D>({ x: 0, y: 0 });
  const targetSizeRef = useRef<Size>({
    width: cursorSize,
    height: cursorSize,
  });

  const cursorToDockProgress = useRef(new Spring(spring));
  const targetXSpring = useRef(new Spring(spring));
  const targetYSpring = useRef(new Spring(spring));
  const targetWSpring = useRef(new Spring(spring));
  const targetHSpring = useRef(new Spring(spring));

  // The stamp size will be snapped to the grid when stamped
  // To minimize the shift on stamp, we pre-size it to fit the grid
  const stampSize = DEFAULT_GRID_RESOLUTION * 5 - BORDER_WIDTH * 2 - GRID_SEAM;

  // Animations derive from user location
  useEffect(() => {
    // if moving/resizing same block, don't animate as we are snapping to grid
    isMovingOrResizing.current = previousBlockId.current === location?.block;
    previousBlockId.current = location?.block;

    // hovering over a block
    cursorToDockProgress.current
      .updateConfig({
        toValue: location?.block && !activeStampMode ? 1 : 0,
      })
      .start();

    if (!location) return;
    let { size, position } = location;

    if (!size || !position) return;

    const blockBorderSize = 2;
    size = {
      width: size.width - GRID_SEAM - 2 * BORDER_WIDTH - 2 * blockBorderSize,
      height: size.height - GRID_SEAM - 2 * BORDER_WIDTH - 2 * blockBorderSize,
    };

    position = {
      x: position.x + 0.5 * GRID_SEAM + BORDER_WIDTH + blockBorderSize,
      y: position.y + 0.5 * GRID_SEAM + BORDER_WIDTH + blockBorderSize,
    };

    targetPositionRef.current = position;
    targetSizeRef.current = size;

    updateSprings({ position, size });
  }, [location, activeStampMode]);

  // Animating the actual DOM elements
  useAnimationFrame(() => {
    const x = lerp(
      cursorPosRef.current.x + DEFAULT_CURSOR_OFFSET,
      isMovingOrResizing.current
        ? targetPositionRef.current.x
        : targetXSpring.current.currentValue,
      cursorToDockProgress.current.currentValue
    );
    const y = lerp(
      cursorPosRef.current.y + DEFAULT_CURSOR_OFFSET,
      isMovingOrResizing.current
        ? targetPositionRef.current.y
        : targetYSpring.current.currentValue,
      cursorToDockProgress.current.currentValue
    );
    const w = lerp(
      cursorSize,
      isMovingOrResizing.current
        ? targetSizeRef.current.width
        : targetWSpring.current.currentValue,
      cursorToDockProgress.current.currentValue
    );
    const h = lerp(
      cursorSize,
      isMovingOrResizing.current
        ? targetSizeRef.current.height
        : targetHSpring.current.currentValue,
      cursorToDockProgress.current.currentValue
    );
    const r = lerp(
      cursorSize / 2,
      1,
      cursorToDockProgress.current.currentValue
    );

    if (
      !containerRef.current ||
      !sizeContainerRef.current ||
      !videoRef.current
    ) {
      return;
    }

    containerRef.current.style.transform = `translate3d(${x}px, ${y}px, 0px)`;
    if (activeStampModeRef.current) {
      sizeContainerRef.current.style.width = `${stampSize}px`;
      sizeContainerRef.current.style.height = `${stampSize}px`;
      videoRef.current.style.borderRadius = `${stampSize}px`;
    } else {
      sizeContainerRef.current.style.width = `${w}px`;
      sizeContainerRef.current.style.height = `${h}px`;
      videoRef.current.style.borderRadius = `${r}px`;
    }
  });

  // If cursor is following mouse, keep updating targetX/Y/W/H to ensure docking animation starts from the latest mouse position
  useAnimationFrame(() => {
    if (
      !cursorToDockProgress.current.isAnimating &&
      cursorToDockProgress.current.currentValue === 0
    ) {
      updateSprings({
        position: {
          x: cursorPosRef.current.x,
          y: cursorPosRef.current.y,
        },
        size: {
          width: cursorSize,
          height: cursorSize,
        },
      });
    }
  });

  // Utility to help update the target x/y/w/h springs
  function updateSprings({
    position: { x, y },
    size: { width, height },
  }: {
    position: Point2D;
    size: Size;
  }) {
    targetXSpring.current.updateConfig({ toValue: x }).start();
    targetYSpring.current.updateConfig({ toValue: y }).start();
    targetWSpring.current.updateConfig({ toValue: width }).start();
    targetHSpring.current.updateConfig({ toValue: height }).start();
  }

  const [user] = useUserInfo(userId);
  const showLock = location?.block;

  return (
    <div
      style={{
        position: "absolute",
        willChange: "transform",
        zIndex: location?.zIndex || BLOCK_Z_UPPERBOUND + 30,
      }}
      ref={containerRef}
    >
      <div
        style={{
          position: "relative",
        }}
        ref={sizeContainerRef}
      >
        <Video
          autoPlay
          muted
          playsInline
          ref={videoRef}
          className={` ${user?.name}`}
          stampMode={activeStampMode ? stampMode : "inactive"}
          docked={!!location?.block}
        />

        {activeStampMode ? (
          <StampMaker upsertBlock={upsertBlock} videoRef={videoRef} />
        ) : null}

        {showLock ? (
          <Lock
            animate={{
              background: location?.locked
                ? colorValue(color, 90)
                : "rgba(0,0,0,.5)",
              opacity: location?.block ? 1 : 0,
              color: location?.locked ? colorValue("paper", 10) : "#FFFFFF",
            }}
            transition={{ spring: DEFAULT_SPRING }}
          >
            {location?.locked ? (
              <Icon icon="Lock" />
            ) : (
              <Icon icon="LockOpen" color="white" />
            )}
          </Lock>
        ) : null}
      </div>
    </div>
  );
}

const stampRaise = 12;
const stampShadowColor = chroma("#503937").alpha(0.8);
const Video = styled("video", {
  position: "absolute",
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "1px",
  transform: "scaleX(-1)",
  boxShadow: "$cursor",
  transitionProperty: "transform, box-shadow, border, outline",
  transitionDuration: "100ms",
  transitionTimingFunction: "ease-in-out",
  border: "1px solid $paper30",
  outline: "solid 2px white",
  outlineOffset: "1px",
  variants: {
    docked: {
      true: {
        border: "1px solid transparent",
        outline: "2px solid transparent",
      },
    },
    stampMode: {
      inactive: {},
      active: {
        transform: `scaleX(-1) translateY(-${stampRaise}px)`,
        boxShadow: `0  ${stampRaise}px 0 ${stampShadowColor}`,
      },
      pressed: {
        transform: `scaleX(-1) translateY(0px)`,
        boxShadow: `0 0 0 ${stampShadowColor}`,
      },
    },
  },
});

const Lock = styled(motion.div, {
  position: "absolute",
  top: "$2",
  left: "$2",
  padding: "$1",
  zIndex: "$blockOverlay",
  borderRadius: "4px",
  backgroundBlur: "",
});

const lerp = (v0: number, v1: number, t: number): number =>
  v0 * (1 - t) + v1 * t;
